/**
 * Streaming Agent UI redesign prototype (?test=streaming-agent)
 *
 * Side-by-side Current vs Minimal for the chat stream density problems
 * captured in bet 2026-07-09-redesign-streaming-agent-ui-ux.
 *
 * Scenarios:
 * - file-edit: accepted change with duplicated MODIFIED + FileEditWidget + stale CTAs
 * - historical: reopened chat with fake [createDocument] narration + yellow retry banner
 * - happy-path: tool rows interleaved with prose (weekly-style)
 * - mid-turn: Cursor-style shimmer status lines before final summary
 * - create-doc: createDocument success chrome (tall preview vs compact row)
 * - cap: tool-budget pause + Continue (stuck Working on it vs partial prose)
 * - soft-errors: Document not found (slug) + YouTube no-captions as alarm red vs muted quiet notes
 */

import { useEffect, useState, type ReactNode } from 'react'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Copy,
  FileCode,
  FileText,
  FolderOpen,
  Loader2,
  Pause,
  RotateCcw,
  Search,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

type Scenario =
  | 'file-edit'
  | 'historical'
  | 'happy-path'
  | 'mid-turn'
  | 'create-doc'
  | 'cap'
  | 'sequencing'
  | 'soft-errors'
type Variant = 'current' | 'minimal'

const DOC_TITLE = '9988076852-investigate-switching-to-deployhq'
const DOC_PATH = 'hop-studios/internal/todos/9988076852-investigate-switching-to-deployhq'
const CREATE_TITLE = 'Rowan + TJ Reviews Meeting - Summary'
const CREATE_PATH = 'hop-studios/users/rowan/meetings/2026-07-09-rowan-tj-reviews-summary'

// ---------------------------------------------------------------------------
// Shared chrome
// ---------------------------------------------------------------------------

function ProtoHeader({
  scenario,
  setScenario,
  variant,
  setVariant,
}: {
  scenario: Scenario
  setScenario: (s: Scenario) => void
  variant: Variant
  setVariant: (v: Variant) => void
}) {
  const scenarios: { id: Scenario; label: string }[] = [
    { id: 'mid-turn', label: 'Mid-turn shimmer' },
    { id: 'file-edit', label: 'File edit (accepted)' },
    { id: 'create-doc', label: 'Create document' },
    { id: 'happy-path', label: 'Happy path density' },
    { id: 'sequencing', label: 'Sequencing / sticky tools' },
    { id: 'cap', label: 'Cap + Continue' },
    { id: 'historical', label: 'Historical / broken tools' },
    { id: 'soft-errors', label: 'Soft errors (doc miss)' },
  ]

  return (
    <div className="border-b bg-sidebar px-4 py-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <a
            href="/?test=index"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid={testIds.streamingAgentPrototype.backToPrototypeIndex}
          >
            <ArrowLeft className="w-4 h-4" />
            Prototype Index
          </a>
          <h1 className="text-xl font-bold mt-1">Streaming Agent UI</h1>
          <p className="text-sm text-muted-foreground">
            Minimal chrome, rich prose - Cursor-style summary of what was done and why.
          </p>
        </div>
        <Badge variant="outline" className="text-xs border-green-500/20 bg-green-500/10 text-green-600">
          Direction approved
        </Badge>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground mr-1">Scenario</span>
        {scenarios.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setScenario(s.id)}
            className={cn(
              'px-3 py-1.5 text-xs rounded-md border transition-colors',
              scenario === s.id
                ? 'bg-foreground text-background border-foreground'
                : 'bg-background border-border hover:border-foreground/40'
            )}
            data-testid={`streaming-agent-scenario-${s.id}`}
          >
            {s.label}
          </button>
        ))}
        <span className="text-xs text-muted-foreground ml-3 mr-1">Variant</span>
        {(['current', 'minimal'] as Variant[]).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVariant(v)}
            className={cn(
              'px-3 py-1.5 text-xs rounded-md border transition-colors capitalize',
              variant === v
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background border-border hover:border-primary/40'
            )}
            data-testid={`streaming-agent-variant-${v}`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  )
}

function UserBubble({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4">
      <div className="inline-block max-w-[92%] rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm">
        {children}
      </div>
    </div>
  )
}

function AssistantProse({ children }: { children: ReactNode }) {
  return <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-snug space-y-2">{children}</div>
}

function DesignNotes({ scenario, variant }: { scenario: Scenario; variant: Variant }) {
  const notes: Record<Scenario, { current: string[]; minimal: string[] }> = {
    'mid-turn': {
      current: [
        'Working on it... shimmer can stick forever if synthesis is dropped',
        'Each tool becomes a bordered peer card while the turn is still running',
        'No clear arc from activity → collapse → prose',
      ],
      minimal: [
        'Quiet shimmer lines (Explored / Reading / Editing) while work is in flight',
        'On complete: collapse into Worked for Ns · N tools, then rich prose',
        'Same compact edit row as other Minimal scenarios',
      ],
    },
    'file-edit': {
      current: [
        'DocumentResultCard (MODIFIED) + FileEditWidget duplicate path/title',
        'After Accept: Accepted badge + Accepted label + Undo + Force apply / Use patch / Cancel still visible',
        'Yellow "3 document reads" batch paints soft tool failures as a warning card',
        'Primary suggestion buttons compete with edit chrome',
      ],
      minimal: [
        'Rich prose first: what changed, decisions, what was skipped',
        'Edit row: filename + +/- + Accepted on line 1; full path on line 2 (no crowding)',
        'Muted thinking collapse; soft failures stay in expand detail, not yellow banner',
        'Suggestion links are quiet text, not primary buttons',
      ],
    },
    'create-doc': {
      current: [
        'Creating: title tool row + tall StreamingOutputWindow preview + Technical details JSON',
        'DocumentResultCard often stacks on top of the preview',
        'Create success competes with the closing summary',
      ],
      minimal: [
        'Prose names the new doc with a link; optional one-line create row (no tall preview by default)',
        'Expand thinking for tool detail; preview is opt-in via Diff / Open',
        'Same density rules as file-edit - one artifact, not three',
      ],
    },
    historical: {
      current: [
        'Persisted narration of failed tool calls becomes a wall of [createDocument] text',
        'Yellow "AI didn\'t execute" banner + Retry CTA on reopen',
        'Looks broken even though the failure already happened days ago',
      ],
      minimal: [
        'Prose explains the failure and what to do next (not a wall of fake tool lines)',
        'Collapsed "Tool calls failed" detail for the stored narration noise',
        'Quiet Retry - no yellow full-width banner',
      ],
    },
    'happy-path': {
      current: [
        'Search / read tool rows sit as peer blocks between answer chunks',
        'Multiple bordered cards compete with markdown',
      ],
      minimal: [
        'Collapsed thinking + long-form prose: included / left out / decisions',
        'One edit row under the summary; tool chrome never peers with the answer',
      ],
    },
    cap: {
      current: [
        'Paused yellow banner with no summary (synthesis discarded client-side)',
        'Working on it... can remain shimmering above the banner',
        'Continue is loud; stream feels stuck',
      ],
      minimal: [
        'Partial findings prose always shows before/with Continue',
        'Muted Continue row (outline, not a second yellow wall)',
        'Thinking collapsed; no stuck Working on it placeholder',
      ],
    },
    sequencing: {
      current: [
        'Closing prose renders first; Searching / create cards glue under the summary',
        'Caused when narration fills the assistant slot early, then tools push after it',
        'Create preview + Keep/Undo sit at the bottom under tool chrome',
      ],
      minimal: [
        'Order: collapsed thinking / tool activity, then rich prose, then compact create/edit row',
        'Tools never stick under the closing summary',
        'Matches live chat after v1.5 turn reorder',
      ],
    },
    'soft-errors': {
      current: [
        'getDocument with a filename slug paints a red Reading document card + Error details JSON',
        'YouTube no-captions batches as yellow warning groups (looks like an outage)',
        'Recoverable misses compete with the assessment prose',
      ],
      minimal: [
        'Same tool-step row as success, but greyscale warning icon (not red alarm)',
        'Italic recovery hint under the row; Details collapsed',
        'Prose assessment stays primary; miss reads as a step that did not block',
      ],
    },
  }

  const list = notes[scenario][variant]
  return (
    <aside
      className="w-full lg:w-72 shrink-0 border-t lg:border-t-0 lg:border-l bg-muted/20 p-4 space-y-3"
      data-testid={testIds.streamingAgentPrototype.designNotes}
    >
      <h2 className="text-sm font-semibold">Design notes</h2>
      <p className="text-xs text-muted-foreground">
        {variant === 'current' ? 'Faithful to production chrome' : 'Target direction for this bet'}
      </p>
      <ul className="space-y-2 text-xs text-muted-foreground list-disc pl-4">
        {list.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </ul>
      <div className="pt-2 border-t text-xs text-muted-foreground space-y-1">
        <p className="font-medium text-foreground/80">Must-haves (locked)</p>
        <p>Minimal chrome, rich closing prose</p>
        <p>Mid-turn shimmer → collapse → summary</p>
        <p>Clickable filename; path on line 2</p>
        <p>Create = compact row, not tall preview</p>
        <p>Cap always shows partial prose + Continue</p>
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// Current (busy) mocks
// ---------------------------------------------------------------------------

function CurrentToolRow({ label, footnote }: { label: string; footnote?: string }) {
  return (
    <div className="my-2">
      <div className="flex items-center gap-2.5 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background/80 border border-border/50 text-green-500">
          <CircleCheck className="h-4 w-4" />
        </div>
        <span className="min-w-0 flex-1 font-medium text-foreground/90">{label}</span>
      </div>
      {footnote && (
        <p className="text-xs text-muted-foreground border-l-2 border-border/80 pl-2 py-0.5 mt-1 max-w-prose">
          {footnote}
        </p>
      )}
    </div>
  )
}

function CurrentYellowBatch({
  label,
  rows,
  expanded = true,
}: {
  label: string
  rows: { ok?: boolean; text: string }[]
  expanded?: boolean
}) {
  const [open, setOpen] = useState(expanded)
  return (
    <div className="my-2 rounded-md border border-yellow-500/20 bg-yellow-500/10 text-sm overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/60"
        onClick={() => setOpen((o) => !o)}
      >
        <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
        <span className="min-w-0 flex-1 text-xs font-medium">{label}</span>
        <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground/50 transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <div className="border-t border-border/40 divide-y divide-border/30">
          {rows.map((r) => (
            <div key={r.text} className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground">
              {r.ok === false ? (
                <AlertTriangle className="h-3 w-3 text-yellow-500 shrink-0" />
              ) : (
                <Check className="h-3 w-3 shrink-0" />
              )}
              <span className={cn('truncate', r.ok === false && 'text-yellow-500')}>{r.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function CurrentModifiedCard() {
  return (
    <div className="group relative rounded-lg border p-4 bg-blue-500/10 border-blue-500/20 text-blue-700 dark:text-blue-400 cursor-pointer">
      <div className="flex items-start gap-3">
        <FileText className="h-5 w-5 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-1">Modified</div>
          <div className="font-medium text-sm mb-1.5 line-clamp-2">{DOC_TITLE}</div>
          <div className="text-xs text-muted-foreground font-mono break-all line-clamp-1">{DOC_PATH}</div>
        </div>
      </div>
    </div>
  )
}

function CurrentFileEditAccepted() {
  return (
    <div className="border border-sidebar-border rounded-lg bg-sidebar p-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">1 File Edited</span>
            <button type="button" className="text-xs text-blue-500 hover:underline">
              Review diff
            </button>
          </div>
          <div className="flex items-center gap-2 pl-6 text-xs text-muted-foreground flex-wrap">
            <FileCode className="w-3.5 h-3.5 shrink-0" />
            <span className="font-mono text-sm text-foreground break-all">{DOC_PATH}</span>
            <span className="text-green-600 font-medium">+4</span>
            <span className="text-red-600 font-medium">-0</span>
          </div>
        </div>
        <Badge variant="outline" className="text-xs border-green-500/20 bg-green-500/10 text-green-600">
          Accepted
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground pl-6">Changes applied.</p>
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60 pl-6">
        <span className="text-xs font-medium text-green-600">Accepted</span>
        <Button type="button" variant="outline" size="sm" className="text-xs h-7 gap-1">
          <RotateCcw className="h-3.5 w-3.5" />
          Undo
        </Button>
      </div>
    </div>
  )
}

function CurrentFileEditScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.currentStream}>
      <UserBubble>
        Update the 9988076852 work spec status to reflect current state: pilot plan is ready, blocked on
        Travis/TJ sync.
      </UserBubble>

      <CurrentToolRow label="Using tool: updateDocumentFrontmatter" />
      <p className="text-xs text-muted-foreground border-l-2 border-border/70 pl-2.5">
        <span className="font-medium text-foreground/65">Context read</span>
        <span className="mx-1 text-border">·</span>
        {DOC_TITLE}
      </p>

      <AssistantProse>
        <p>
          Frontmatter <code>status</code> → <code>in_progress</code> (applied). Body edit pending acceptance.
        </p>
      </AssistantProse>

      <div className="space-y-2">
        <CurrentModifiedCard />
        <CurrentFileEditAccepted />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="text-xs h-8">
          Force apply anyway
        </Button>
        <Button size="sm" variant="outline" className="text-xs h-8">
          Use patch instead
        </Button>
        <Button size="sm" variant="outline" className="text-xs h-8">
          Cancel
        </Button>
      </div>

      <CurrentYellowBatch
        label="3 document reads"
        rows={[
          { text: `Reading document... · ${DOC_TITLE}` },
          { ok: false, text: 'Section not found: "Current Status"' },
          { text: 'Read tool result 14a05c2f… · ~1716 tokens' },
        ]}
      />
    </div>
  )
}

function CurrentHistoricalScenario() {
  const fakeLines = [
    '[Creating raw transcript document...]',
    '[createDocument - Raw Transcript]',
    '[createDocument - Summary]',
    'I need to stop narrating and just call the tools.',
    '[Calling createDocument...]',
    '[createDocument invocation 2]',
    'Apologies for the loop above — something is preventing me from calling the tools...',
    '[Calling createDocument for raw transcript and summary in sequence]',
  ]

  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.currentStream}>
      <UserBubble>Create raw + summary meeting docs under hop-studios/users/rowan/meetings/</UserBubble>
      <AssistantProse>
        <p>I&apos;ll create both the raw transcript and summary docs now...</p>
      </AssistantProse>
      <div className="space-y-2 text-sm text-foreground/90 border-y border-border/40 py-2 divide-y divide-border/30">
        {fakeLines.map((line) => (
          <p key={line} className="py-1.5 font-mono text-xs text-muted-foreground">
            {line}
          </p>
        ))}
      </div>
      <div className="flex gap-2 opacity-70">
        <Button variant="ghost" size="sm" className="h-7 text-xs">
          <RotateCcw className="h-3 w-3 mr-1" />
          Regenerate
        </Button>
        <Button variant="ghost" size="sm" className="h-7 text-xs">
          <Copy className="h-3 w-3 mr-1" />
          Copy
        </Button>
      </div>
      <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium">AI didn&apos;t execute the action</div>
            <div className="text-xs text-muted-foreground mt-1">
              The AI described what it would do but didn&apos;t call the tool. Try being more explicit.
            </div>
            <Button size="sm" variant="outline" className="text-xs mt-2">
              Retry with explicit tool call
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function CurrentHappyPathScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.currentStream}>
      <UserBubble>Draft a staff meeting entry from recent activity in clients/</UserBubble>
      <CurrentToolRow label="Searching: completed launched shipped deployed fixed" footnote="8 matches" />
      <CurrentToolRow label="Searching: FlowNotes documents" footnote="5 matches" />
      <p className="text-xs text-muted-foreground border-l-2 border-border/70 pl-2.5">2 document reads</p>
      <AssistantProse>
        <p>
          <strong>What I found:</strong> three shipped items and one open blocker worth calling out.
        </p>
        <ul>
          <li>Speedy static cache hygiene - shipped</li>
          <li>JCOGS image control panel - still open</li>
        </ul>
      </AssistantProse>
      <CurrentToolRow label="Searching: FlowNotes documents" footnote="3 matches" />
      <p className="text-xs text-muted-foreground border-l-2 border-border/70 pl-2.5">3 document reads</p>
      <AssistantProse>
        <p>Draft written into Weekly Priorities &amp; Notes.</p>
      </AssistantProse>
      <CurrentModifiedCard />
      <div className="border border-sidebar-border rounded-lg bg-sidebar p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium">1 File Edited</span>
          <button type="button" className="text-xs text-blue-500 hover:underline">
            Review diff
          </button>
          <span className="text-green-600 text-xs ml-auto">+12</span>
          <span className="text-red-600 text-xs">-0</span>
        </div>
        <div className="flex gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" className="text-xs h-7">
            Undo
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-7">
            Review Changes
          </Button>
          <Button size="sm" className="text-xs h-7 bg-green-600 hover:bg-green-700 text-white">
            Keep
          </Button>
        </div>
      </div>
    </div>
  )
}

function CurrentMidTurnScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.currentStream}>
      <UserBubble>Draft a staff meeting entry from recent activity in clients/</UserBubble>
      <p className="text-sm text-muted-foreground italic animate-pulse">Working on it...</p>
      <CurrentToolRow label="Searching: completed launched shipped" />
      <div className="flex items-center gap-2.5 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="font-medium text-foreground/90">Reading document...</span>
      </div>
      <CurrentToolRow label="Searching: FlowNotes documents" footnote="still running..." />
      <p className="text-xs text-muted-foreground">No closing summary yet - stream still filling with tool cards.</p>
    </div>
  )
}

function CurrentCreateDocScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.currentStream}>
      <UserBubble>Create a meeting summary under hop-studios/users/rowan/meetings/</UserBubble>
      <CurrentToolRow label={`Creating: ${CREATE_TITLE}`} />
      <div className="rounded-lg border border-border bg-muted/30 overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/50">
          <button type="button" className="text-sm font-medium truncate hover:underline text-left">
            {CREATE_PATH}
          </button>
          <span className="text-xs text-green-600 font-medium">+100</span>
        </div>
        <div className="p-3 font-mono text-xs leading-relaxed h-[125px] overflow-hidden text-muted-foreground whitespace-pre">
          {`# Rowan + TJ Reviews Meeting - Summary\n\n**Date:** 2026-07-09\n**Attendees:** Rowan, TJ\n\n## Decisions\n- Pilot DeployHQ on ee7...\n`}
        </div>
      </div>
      <details className="text-sm">
        <summary className="text-xs text-muted-foreground cursor-pointer">Technical details</summary>
        <pre className="mt-2 p-3 bg-muted rounded-md text-xs overflow-auto">{`{\n  "success": true,\n  "documentId": "b24ee3eb-..."\n}`}</pre>
      </details>
      <div className="group relative rounded-lg border p-4 bg-green-500/10 border-green-500/20 text-green-700 dark:text-green-400">
        <div className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-1">Created</div>
        <div className="font-medium text-sm">{CREATE_TITLE}</div>
        <div className="text-xs text-muted-foreground font-mono mt-1">{CREATE_PATH}</div>
      </div>
      <AssistantProse>
        <p>Document created successfully.</p>
      </AssistantProse>
    </div>
  )
}

function CurrentCapScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.currentStream}>
      <UserBubble>Audit all DeployHQ / Deploybot docs and summarize open blockers across related todos.</UserBubble>
      <CurrentToolRow label="Searching: deployhq deploybot" footnote="8 matches" />
      <CurrentYellowBatch
        label="17 Basecamp lookups"
        expanded={false}
        rows={[{ text: 'fetchBasecampTodo ×17' }]}
      />
      <p className="text-sm text-muted-foreground italic animate-pulse">Working on it...</p>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-yellow-500/20 bg-yellow-500/10 px-3 py-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-yellow-500">
          <Pause className="h-3.5 w-3.5 shrink-0 opacity-80" />
          <span>Paused - tool limit reached. Narrow the request or continue the pass.</span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1.5 border-yellow-500/20 text-yellow-500 hover:bg-yellow-500/10"
        >
          <RotateCcw className="h-3 w-3" />
          Continue
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        No partial summary - synthesis tokens were discarded after the error event.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Minimal mocks
// ---------------------------------------------------------------------------

function MinimalThinking({
  summary,
  children,
  defaultOpen = false,
}: {
  summary: string
  children?: ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="my-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
        {summary}
      </button>
      {open && children && <div className="mt-1.5 ml-5 space-y-1 text-xs text-muted-foreground">{children}</div>}
    </div>
  )
}

function MinimalEditRow({
  status,
  title = DOC_TITLE,
  path = DOC_PATH,
  additions = 4,
  deletions = 0,
}: {
  status: 'pending' | 'accepted'
  title?: string
  path?: string
  additions?: number
  deletions?: number
}) {
  return (
    <div
      className="my-3 border-y border-border/50 py-2.5 space-y-0.5"
      data-testid={testIds.streamingAgentPrototype.minimalEditRow}
    >
      {/* Line 1: filename + stats + status/actions (never compete with path) */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <button
          type="button"
          className="font-medium text-foreground hover:underline text-left truncate min-w-0 max-w-[min(100%,28rem)]"
        >
          {title}
        </button>
        <span className="text-xs tabular-nums shrink-0">
          <span className="text-green-600/80">+{additions}</span>
          <span className="mx-1 text-muted-foreground/50">/</span>
          <span className="text-red-600/80">-{deletions}</span>
        </span>
        {status === 'pending' ? (
          <span className="flex items-center gap-1.5 ml-auto shrink-0">
            <button type="button" className="text-xs text-muted-foreground hover:text-foreground">
              Diff
            </button>
            <button type="button" className="text-xs text-muted-foreground hover:text-foreground">
              Undo
            </button>
            <button type="button" className="text-xs font-medium text-foreground hover:underline">
              Keep
            </button>
          </span>
        ) : (
          <span className="flex items-center gap-2 ml-auto shrink-0 text-xs text-muted-foreground">
            <span>Accepted</span>
            <button type="button" className="hover:text-foreground inline-flex items-center gap-1">
              <RotateCcw className="h-3 w-3" />
              Undo
            </button>
          </span>
        )}
      </div>
      {/* Line 2: full path - clickable, never truncates the status row */}
      <button
        type="button"
        className="block text-xs text-muted-foreground font-mono hover:text-foreground hover:underline text-left break-all"
        title={path}
      >
        {path}
      </button>
    </div>
  )
}

function MinimalFileEditScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.minimalStream}>
      <UserBubble>
        Update the 9988076852 work spec status to reflect current state: pilot plan is ready, blocked on
        Travis/TJ sync.
      </UserBubble>

      <MinimalThinking summary="Worked for 12s · 4 tools">
        <p>updateDocumentFrontmatter · status → in_progress</p>
        <p>getDocument · {DOC_TITLE}</p>
        <p className="text-muted-foreground/80">Section lookup missed &quot;Current Status&quot; (non-blocking)</p>
        <p>readToolResult · ~1716 tokens</p>
      </MinimalThinking>

      <AssistantProse>
        <p>
          <strong>Done.</strong> Frontmatter is <code>in_progress</code>, and I proposed a short status note
          under Basecamp todo notes for the Travis/TJ sync blocker.
        </p>
        <p>
          <strong>What I changed</strong>
        </p>
        <ul>
          <li>
            Frontmatter <code>status</code> → <code>in_progress</code> (applied immediately - metadata only)
          </li>
          <li>
            Body: new <code>### Status update — 2026-07-09</code> under Basecamp todo notes (pending Keep)
          </li>
        </ul>
        <p>
          <strong>Decisions</strong>
        </p>
        <ul>
          <li>
            Appended a dated status subsection instead of rewriting the existing bullets - avoids the
            large-deletion guard and keeps history intact.
          </li>
          <li>
            Looked for a heading named &quot;Current Status&quot; first; the doc uses{' '}
            <code>## Basecamp todo notes</code>, so I targeted that section instead (soft miss, not a
            failure).
          </li>
          <li>
            Did not touch due date or assignees - those still live in Basecamp as source of truth.
          </li>
        </ul>
        <p>
          <strong>Left alone:</strong> pilot plan steps, pricing table, and open questions - already
          accurate per TJ&apos;s Jun 17 comment.
        </p>
        <p>
          Review the edit below, then Keep if it looks right. Next useful follow-ups: draft Basecamp notes,
          or re-fetch comments after TJ meets Travis.
        </p>
      </AssistantProse>

      <MinimalEditRow status="accepted" />

      <div className="flex flex-wrap gap-3 text-xs">
        <button type="button" className="text-muted-foreground hover:text-foreground hover:underline">
          Draft Basecamp notes for 9988076852
        </button>
        <button type="button" className="text-muted-foreground hover:text-foreground hover:underline">
          Fetch todo comments after TJ meets Travis
        </button>
      </div>
    </div>
  )
}

function MinimalHistoricalScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.minimalStream}>
      <UserBubble>Create raw + summary meeting docs under hop-studios/users/rowan/meetings/</UserBubble>

      <MinimalThinking summary="Tool calls failed · 0 documents created">
        <p>Model narrated createDocument repeatedly without emitting tool calls.</p>
        <p className="text-muted-foreground/70">Collapsed 40+ fake [createDocument] lines from stored transcript.</p>
      </MinimalThinking>

      <AssistantProse>
        <p>
          <strong>Nothing was created.</strong> This turn never reached a real <code>createDocument</code>{' '}
          call - the model kept describing the action in text instead of invoking the tool.
        </p>
        <p>
          <strong>What happened</strong>
        </p>
        <ul>
          <li>Intended path: raw transcript + summary under <code>hop-studios/users/rowan/meetings/</code></li>
          <li>Actual path: a long narration loop with placeholder lines like [Calling createDocument...]</li>
          <li>On reopen, that narration was stored as assistant text - it looks like a broken UI, but it is
            just failed tool intent with no tool results attached.</li>
        </ul>
        <p>
          <strong>What I would do on retry</strong>
        </p>
        <ul>
          <li>Call <code>createDocument</code> twice (raw, then summary) with no preamble</li>
          <li>Reply only after both succeed, with links to the new docs</li>
        </ul>
        <p>
          Use Retry below if you still want those two files created. I collapsed the old fake tool lines so
          this thread stays readable.
        </p>
      </AssistantProse>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>Tools were not called</span>
        <button type="button" className="hover:text-foreground hover:underline">
          Retry with explicit tool call
        </button>
        <button type="button" className="hover:text-foreground inline-flex items-center gap-1">
          <Copy className="h-3 w-3" />
          Copy
        </button>
      </div>
    </div>
  )
}

function MinimalHappyPathScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.minimalStream}>
      <UserBubble>Draft a staff meeting entry from recent activity in clients/</UserBubble>

      <MinimalThinking summary="Worked for 48s · 6 tools">
        <p className="inline-flex items-center gap-1">
          <Search className="h-3 w-3" /> 2 searches · completed / launched / shipped
        </p>
        <p>5 document reads</p>
        <p>1 document edit · Weekly Priorities &amp; Notes</p>
      </MinimalThinking>

      <AssistantProse>
        <p>
          Drafted a staff-meeting blurb into{' '}
          <button type="button" className="font-medium text-foreground underline underline-offset-2">
            Weekly Priorities &amp; Notes
          </button>{' '}
          from recent client activity. Here is what I used and why.
        </p>
        <p>
          <strong>What I included</strong>
        </p>
        <ul>
          <li>
            <strong>Speedy static cache hygiene</strong> - shipped this week; clear win for the meeting
          </li>
          <li>
            <strong>JCOGS image control panel</strong> - still open / memory risk; worth a verbal callout
          </li>
          <li>
            <strong>DeployHQ investigation</strong> - status note only (blocked on Travis/TJ sync), not a
            deep dive
          </li>
        </ul>
        <p>
          <strong>What I left out</strong>
        </p>
        <ul>
          <li>Routine Basecamp triage noise with no decision or ship signal</li>
          <li>Duplicate DeployBot docs we already archived earlier in this thread</li>
          <li>Anything still marked draft with no client-visible change</li>
        </ul>
        <p>
          <strong>Decisions</strong>
        </p>
        <ul>
          <li>Scoped search to completed / launched / shipped / fixed keywords, then spot-read the hits</li>
          <li>Preferred items with a clear status change over long-running investigations</li>
          <li>Wrote into the existing weekly doc instead of creating a new meeting note</li>
        </ul>
        <p>
          Keep the edit below if the tone matches what you want in standup. I can tighten further or pull
          one more client folder if something is missing.
        </p>
      </AssistantProse>

      <MinimalEditRow
        status="pending"
        title="Weekly Priorities & Notes"
        path="hop-studios/users/rowan/weekly"
        additions={12}
        deletions={0}
      />
    </div>
  )
}

function ShimmerLine({
  icon,
  children,
  active,
}: {
  icon: ReactNode
  children: ReactNode
  active?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 text-xs text-muted-foreground py-0.5',
        active && 'animate-pulse'
      )}
    >
      <span className="shrink-0 opacity-70">{icon}</span>
      <span>{children}</span>
    </div>
  )
}

function MinimalMidTurnScenario() {
  const [phase, setPhase] = useState(0)

  useEffect(() => {
    const timers = [
      window.setTimeout(() => setPhase(1), 900),
      window.setTimeout(() => setPhase(2), 1800),
      window.setTimeout(() => setPhase(3), 2700),
      window.setTimeout(() => setPhase(4), 3600),
    ]
    return () => timers.forEach((t) => window.clearTimeout(t))
  }, [])

  const done = phase >= 4

  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.minimalStream}>
      <UserBubble>Draft a staff meeting entry from recent activity in clients/</UserBubble>

      {!done ? (
        <div className="space-y-1 pl-0.5" data-testid={testIds.streamingAgentPrototype.midTurnShimmer}>
          {phase >= 0 && (
            <ShimmerLine icon={<Search className="h-3 w-3" />} active={phase === 0}>
              Searching completed / launched / shipped…
            </ShimmerLine>
          )}
          {phase >= 1 && (
            <ShimmerLine icon={<FolderOpen className="h-3 w-3" />} active={phase === 1}>
              Explored 5 files
            </ShimmerLine>
          )}
          {phase >= 2 && (
            <ShimmerLine icon={<FileText className="h-3 w-3" />} active={phase === 2}>
              Reading JCOGS image control panel…
            </ShimmerLine>
          )}
          {phase >= 3 && (
            <ShimmerLine icon={<FileCode className="h-3 w-3" />} active={phase === 3}>
              Editing Weekly Priorities &amp; Notes
            </ShimmerLine>
          )}
        </div>
      ) : (
        <>
          <MinimalThinking summary="Worked for 48s · 6 tools">
            <p>2 searches · completed / launched / shipped</p>
            <p>5 document reads</p>
            <p>1 document edit · Weekly Priorities &amp; Notes</p>
          </MinimalThinking>

          <AssistantProse>
            <p>
              <strong>Done.</strong> Drafted a staff-meeting blurb into Weekly Priorities &amp; Notes from
              recent client activity.
            </p>
            <p>
              <strong>What I included:</strong> Speedy static cache (shipped), JCOGS image panel (open
              blocker), DeployHQ status note only.
            </p>
            <p>
              <strong>Decisions:</strong> Preferred clear status changes over long investigations; wrote
              into the existing weekly doc instead of a new meeting note.
            </p>
            <p>
              <strong>Left out:</strong> Routine triage noise and already-archived DeployBot duplicates.
            </p>
          </AssistantProse>

          <MinimalEditRow
            status="pending"
            title="Weekly Priorities & Notes"
            path="hop-studios/users/rowan/weekly"
            additions={12}
            deletions={0}
          />

          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
            onClick={() => setPhase(0)}
          >
            Replay shimmer
          </button>
        </>
      )}
    </div>
  )
}

function MinimalCreateDocScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.minimalStream}>
      <UserBubble>Create a meeting summary under hop-studios/users/rowan/meetings/</UserBubble>

      <MinimalThinking summary="Worked for 9s · 2 tools">
        <p>searchDocuments · meetings context</p>
        <p>createDocument · {CREATE_TITLE}</p>
      </MinimalThinking>

      <AssistantProse>
        <p>
          <strong>Created</strong>{' '}
          <button type="button" className="font-medium underline underline-offset-2">
            {CREATE_TITLE}
          </button>
          .
        </p>
        <p>
          <strong>What&apos;s in it</strong>
        </p>
        <ul>
          <li>Attendees, date, and decision log from the sync</li>
          <li>Open blockers (Travis/TJ DeployHQ kickoff)</li>
          <li>Links back to the raw transcript (create that next if you want both)</li>
        </ul>
        <p>
          <strong>Decisions</strong>
        </p>
        <ul>
          <li>Filed under <code>hop-studios/users/rowan/meetings/</code> (multi-topic internal sync)</li>
          <li>Summary-only for now - raw transcript is a separate create if you still want it</li>
        </ul>
      </AssistantProse>

      <MinimalEditRow
        status="accepted"
        title={CREATE_TITLE}
        path={CREATE_PATH}
        additions={100}
        deletions={0}
      />
    </div>
  )
}

function MinimalCapScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.minimalStream}>
      <UserBubble>Audit all DeployHQ / Deploybot docs and summarize open blockers across related todos.</UserBubble>

      <MinimalThinking summary="Paused after 40 tools · partial findings">
        <p>8 document searches</p>
        <p>17 Basecamp lookups</p>
        <p>12 document reads</p>
        <p className="text-muted-foreground/80">Stopped at tool budget - synthesis still rendered</p>
      </MinimalThinking>

      <AssistantProse>
        <p>
          <strong>Partial findings</strong> (tool limit reached before a full pass):
        </p>
        <ul>
          <li>
            Canonical research lives on <strong>8533857341</strong> (dev discussion) - duplicate under
            hop-business already archived earlier
          </li>
          <li>
            Active follow-up is <strong>9988076852</strong> - pilot plan ready, blocked on Travis/TJ sync
          </li>
          <li>Pricing and ee7 pilot steps are well documented; execution has not started</li>
        </ul>
        <p>
          <strong>Not covered yet:</strong> full comment threads on older Basecamp todos, and a pass over
          client-folder mentions of DeployBot.
        </p>
        <p>
          Continue to finish the remaining lookups, or narrow to one todo if you only need the active
          pilot status.
        </p>
      </AssistantProse>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground border-y border-border/50 py-2">
        <Pause className="h-3.5 w-3.5 shrink-0 opacity-70" />
        <span>Tool limit reached</span>
        <button type="button" className="ml-auto font-medium text-foreground hover:underline">
          Continue
        </button>
      </div>
    </div>
  )
}

function CurrentSequencingScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.currentStream}>
      <UserBubble>
        Research kindred projects to the lightweb in ethics, systems, technology
      </UserBubble>

      <AssistantProse>
        <p>
          <strong>What I found:</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Platform Cooperativism</strong> - worker-owned digital platforms
          </li>
          <li>
            <strong>Decentralized Infrastructure</strong> - privacy-first community stacks
          </li>
          <li>
            <strong>Platform Socialism</strong> - public / commons digital services
          </li>
        </ul>
        <p>
          Shared belief: technology should serve communities, not extract from them.
        </p>
      </AssistantProse>

      <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm flex items-center gap-2">
        <Check className="h-3.5 w-3.5 text-green-600" />
        Searching: FlowNotes documents
      </div>
      <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm flex items-center gap-2">
        <Check className="h-3.5 w-3.5 text-green-600" />
        Searching YouTube: cooperative technology ethics privacy
      </div>
      <div className="rounded-md border border-yellow-500/20 bg-yellow-500/10 px-3 py-2 text-sm flex items-center gap-2">
        <ChevronRight className="h-3.5 w-3.5" />
        2 document reads
      </div>
      <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm flex items-center gap-2">
        <Check className="h-3.5 w-3.5 text-green-600" />
        Creating: 2026-07-09-kindred-projects-ethics-systems-technology
      </div>
      <div className="rounded-lg border border-border bg-muted/20 p-3 font-mono text-xs text-muted-foreground">
        Tall create preview glued under the summary...
      </div>
      <div className="flex items-center gap-2 text-sm border rounded-md px-3 py-2">
        <span>1 File</span>
        <span className="ml-auto flex gap-2 text-xs">
          <button type="button">Undo</button>
          <button type="button" className="font-medium">
            Keep
          </button>
          <button type="button" className="text-blue-500">
            Review
          </button>
        </span>
      </div>
    </div>
  )
}

function MinimalSequencingScenario() {
  return (
    <div className="space-y-3" data-testid={testIds.streamingAgentPrototype.minimalStream}>
      <UserBubble>
        Research kindred projects to the lightweb in ethics, systems, technology
      </UserBubble>

      <MinimalThinking summary="Worked for 42s · 5 tools">
        <p>2 searches · FlowNotes + YouTube</p>
        <p>2 document reads</p>
        <p>1 document create · kindred-projects note</p>
      </MinimalThinking>

      <AssistantProse>
        <p>
          <strong>What I found:</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Platform Cooperativism</strong> - worker-owned digital platforms
          </li>
          <li>
            <strong>Decentralized Infrastructure</strong> - privacy-first community stacks
          </li>
          <li>
            <strong>Platform Socialism</strong> - public / commons digital services
          </li>
        </ul>
        <p>
          <strong>Decisions:</strong> Preferred movements with concrete org models over
          abstract ethics essays; wrote a short research note instead of dumping links only.
        </p>
        <p>
          <strong>Left out:</strong> Generic Web3 marketing and duplicate Basecamp threads.
        </p>
      </AssistantProse>

      <MinimalEditRow
        status="pending"
        title="2026-07-09-kindred-projects-ethics-systems-technology"
        path="hop-studios/users/rowan/research/2026-07-09-kindred-projects-ethics-systems-technology"
        additions={48}
        deletions={0}
      />
    </div>
  )
}

const SOFT_ERR_DOC_JSON = `{
  "success": false,
  "error": "Document not found",
  "tool": "resolveDocumentIdForWrite",
  "requestedDocumentId": "2026-07-12-whole-egg-hollandaise-immersion-blender",
  "parsedAsUuid": false,
  "candidatesFromContext": [],
  "hint": "Re-run searchDocuments with pathContains or fileName..."
}`

function CurrentSoftErrorsScenario() {
  return (
    <div data-testid={testIds.streamingAgentPrototype.currentStream}>
      <UserBubble>
        <span className="inline-flex items-center rounded-full bg-violet-500/15 text-violet-600 dark:text-violet-400 px-2 py-0.5 text-[11px] font-medium mr-2">
          YouTube Research
        </span>
        see the recipes folder for the hollandaise document, assess it and let me know if it
        aligns with what you were able to find
      </UserBubble>

      {/* Alarm-red single getDocument miss (as live chat looked 2026-07-12) */}
      <div className="my-2">
        <div className="flex items-center gap-2.5 rounded-md border border-red-500/25 bg-red-500/[0.06] px-3 py-2 text-sm">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background/80 border border-border/50 text-red-500">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <span className="min-w-0 flex-1 font-medium text-foreground/90">Reading document...</span>
        </div>
        <p className="text-xs text-red-500 mt-1.5 max-w-prose">Document not found</p>
        <details open className="mt-1 text-sm">
          <summary className="text-xs text-muted-foreground cursor-pointer py-1">Error details</summary>
          <pre className="mt-1 p-3 bg-muted rounded-md text-[11px] font-mono overflow-auto max-w-2xl whitespace-pre-wrap">
            {SOFT_ERR_DOC_JSON}
          </pre>
        </details>
      </div>

      <CurrentYellowBatch
        label="3 document reads"
        rows={[
          { ok: false, text: 'No captions available for video rOWzVV_XrcM' },
          { ok: false, text: 'No captions available for video qPn2D6JmHRg' },
          { ok: false, text: 'No captions available for video 7WeebHeazro' },
        ]}
      />
      <CurrentYellowBatch
        label="5 document reads"
        expanded={false}
        rows={[
          { ok: false, text: 'No captions available for video K_RvZYrOF7I' },
          { ok: false, text: 'No captions available for video jfZgeTq2Wn0' },
        ]}
      />

      <CurrentToolRow label="Searching: FlowNotes documents" footnote="1 match" />
      <p className="text-xs text-muted-foreground border-l-2 border-border/70 pl-2.5 my-1">
        <span className="font-medium text-foreground/65">Context read</span>
        <span className="mx-1 text-border">·</span>
        2026-07-12 Whole Egg Hollandaise - personal/recipes/...
      </p>

      <AssistantProse>
        <p>
          The document is solid and aligns well with what I found. Here&apos;s my assessment...
        </p>
      </AssistantProse>
    </div>
  )
}

function MinimalSoftErrorsScenario() {
  return (
    <div data-testid={testIds.streamingAgentPrototype.minimalStream}>
      <UserBubble>
        <span className="inline-flex items-center rounded-full bg-violet-500/15 text-violet-600 dark:text-violet-400 px-2 py-0.5 text-[11px] font-medium mr-2">
          YouTube Research
        </span>
        see the recipes folder for the hollandaise document, assess it and let me know if it
        aligns with what you were able to find
      </UserBubble>

      <MinimalThinking summary="Worked for 38s · 10 tools" defaultOpen>
        <p className="flex items-center gap-1.5 text-muted-foreground/80">
          <AlertTriangle className="h-3 w-3 text-muted-foreground/60 shrink-0" />
          Reading document · Not found - search by path or attach the doc
        </p>
        <p className="flex items-center gap-1.5 text-muted-foreground/80">
          <AlertTriangle className="h-3 w-3 text-muted-foreground/60 shrink-0" />
          8 transcript fetches · no captions (non-blocking)
        </p>
        <p className="text-muted-foreground/80">searchDocuments · 1 match · recipes hollandaise</p>
      </MinimalThinking>

      <div className="my-2">
        <div className="flex items-center gap-2.5 rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background/80 border border-border/50 text-muted-foreground/70">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <span className="min-w-0 flex-1 font-medium text-foreground/75">Reading document...</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground/80 italic">
          Not found - search by path or attach the doc
        </p>
      </div>
      <p className="text-xs text-muted-foreground border-l-2 border-border/70 pl-2.5 my-1.5 max-w-prose leading-snug">
        <span className="font-medium text-foreground/65">Context read</span>
        <span className="mx-1 text-border">·</span>
        2026-07-12 Whole Egg Hollandaise - personal/recipes/...
      </p>

      <AssistantProse>
        <p>
          The document is solid and aligns well with what I found from video metadata and the
          recipe note.
        </p>
        <p>
          <strong>Strong alignment:</strong> hot butter stream, narrow jar, fast emulsification -
          matches Serious Eats / kitchenflash framing.
        </p>
        <p>
          <strong>Ratios:</strong> 100-113 g butter per whole egg is a sensible adaptation of the
          yolk-only Kenji range.
        </p>
        <p className="text-muted-foreground">
          Soft tool misses (slug-as-id, caption gaps) stay in the quiet rail above - they do not
          paint the turn as failed.
        </p>
      </AssistantProse>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function renderStream(scenario: Scenario, variant: Variant) {
  if (variant === 'current') {
    switch (scenario) {
      case 'mid-turn':
        return <CurrentMidTurnScenario />
      case 'file-edit':
        return <CurrentFileEditScenario />
      case 'create-doc':
        return <CurrentCreateDocScenario />
      case 'happy-path':
        return <CurrentHappyPathScenario />
      case 'sequencing':
        return <CurrentSequencingScenario />
      case 'cap':
        return <CurrentCapScenario />
      case 'historical':
        return <CurrentHistoricalScenario />
      case 'soft-errors':
        return <CurrentSoftErrorsScenario />
    }
  }
  switch (scenario) {
    case 'mid-turn':
      return <MinimalMidTurnScenario />
    case 'file-edit':
      return <MinimalFileEditScenario />
    case 'create-doc':
      return <MinimalCreateDocScenario />
    case 'happy-path':
      return <MinimalHappyPathScenario />
    case 'sequencing':
      return <MinimalSequencingScenario />
    case 'cap':
      return <MinimalCapScenario />
    case 'historical':
      return <MinimalHistoricalScenario />
    case 'soft-errors':
      return <MinimalSoftErrorsScenario />
  }
}

export function StreamingAgentTest() {
  const [scenario, setScenario] = useState<Scenario>('mid-turn')
  const [variant, setVariant] = useState<Variant>('minimal')

  return (
    <div
      className="h-screen flex flex-col bg-background"
      data-testid={testIds.streamingAgentPrototype.root}
    >
      <ProtoHeader
        scenario={scenario}
        setScenario={setScenario}
        variant={variant}
        setVariant={setVariant}
      />

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        <div className="flex-1 overflow-auto">
          <div className="max-w-3xl mx-auto px-4 py-6">{renderStream(scenario, variant)}</div>
        </div>
        <DesignNotes scenario={scenario} variant={variant} />
      </div>
    </div>
  )
}
